import './styles.css';
import questionsData from '../questions.json';
import type { FormData, FormSchema, Question } from './types';
import { validateQuestion } from './validators';
import { renderStep, type StepHandle } from './form-renderer';

const BRIDGE_PAGE_URL = '/bridge.html';
const WEBHOOK_URL =
  'https://services.leadconnectorhq.com/hooks/50iga0fTIPbFyOgsOFDB/webhook-trigger/52cc7f1d-5cb8-4bce-a805-2cc096c54e12';

const schema = questionsData as FormSchema;
const questions: Question[] = schema.form.questions;

class SurveyEngine {
  private readonly steps: StepHandle[] = [];
  private currentStep = 1;
  private readonly formData: FormData = {};
  private consentGiven = false;

  private readonly stepContainer: HTMLElement;
  private readonly progressFill: HTMLElement;
  private readonly stepLabel: HTMLElement;
  private readonly percentLabel: HTMLElement;

  constructor() {
    this.stepContainer = requireEl('#stepContainer');
    this.progressFill = requireEl('#progressFill');
    this.stepLabel = requireEl('#stepLabel');
    this.percentLabel = requireEl('#percentLabel');

    for (const question of questions) {
      const handle = renderStep(question, questions.length, {
        onValueChange: (field, value) => this.handleValueChange(field, value),
        onConsentChange: consented => this.handleConsentChange(consented),
        onNext: () => this.goNext(),
        onBack: () => this.goBack(),
        onSubmit: () => this.submit(),
      });
      this.steps.push(handle);
      this.stepContainer.appendChild(handle.element);
    }

    this.activate(1);
  }

  private handleValueChange(fieldName: string, value: string): void {
    this.formData[fieldName] = value;
    const handle = this.findHandleByField(fieldName);
    if (!handle) return;

    const result = validateQuestion(handle.question, value);
    handle.setValid(result.valid, result.message);
    this.updateStepEnable(handle);
  }

  private handleConsentChange(consented: boolean): void {
    this.consentGiven = consented;
    const phoneHandle = this.steps.find(s => s.question.type === 'tel');
    if (phoneHandle) this.updateStepEnable(phoneHandle);
  }

  private updateStepEnable(handle: StepHandle): void {
    const value = this.formData[handle.question.field_name] ?? '';
    const result = validateQuestion(handle.question, value);
    let canProceed = result.valid;
    if (handle.question.type === 'tel' && handle.question.consent_required && !this.consentGiven) {
      canProceed = false;
    }
    handle.enableSubmit(canProceed);
  }

  private findHandleByField(fieldName: string): StepHandle | undefined {
    return this.steps.find(s => s.question.field_name === fieldName);
  }

  private activate(stepNumber: number): void {
    for (const handle of this.steps) {
      handle.element.classList.toggle('active', handle.question.step === stepNumber);
    }
    this.currentStep = stepNumber;
    this.updateProgress();
    this.scrollToTop();
  }

  private updateProgress(): void {
    const total = questions.length;
    const display = Math.min(this.currentStep, total);
    const pct = Math.round((display / total) * 100);
    this.progressFill.style.width = `${pct}%`;
    this.stepLabel.textContent = `Step ${display} of ${total}`;
    this.percentLabel.textContent = `${pct}%`;
  }

  private scrollToTop(): void {
    const survey = document.querySelector<HTMLElement>('.survey-section');
    if (!survey) return;
    window.scrollTo({ top: survey.offsetTop - 20, behavior: 'smooth' });
  }

  private goNext(): void {
    if (this.currentStep < questions.length) this.activate(this.currentStep + 1);
  }

  private goBack(): void {
    if (this.currentStep > 1) this.activate(this.currentStep - 1);
  }

  private submit(): void {
    // Final validation pass — ensure every required field is valid.
    for (const handle of this.steps) {
      const value = this.formData[handle.question.field_name] ?? '';
      const result = validateQuestion(handle.question, value);
      if (handle.question.required && !result.valid) {
        handle.setValid(false, result.message || 'This field is required');
        this.activate(handle.question.step);
        return;
      }
    }

    if (!this.consentGiven) return;

    void this.postToWebhook().finally(() => {
      window.location.assign(BRIDGE_PAGE_URL);
    });
  }

  /**
   * Posts the survey data to the GoHighLevel webhook.
   *
   * DEBUG MODE: uses `await fetch` and logs the full response to the console so
   * we can see exactly what GHL returns. Once the webhook is confirmed working,
   * swap this back to `navigator.sendBeacon` for fire-and-forget posting that
   * survives the redirect without delaying the user.
   */
  private async postToWebhook(): Promise<void> {
    const body = JSON.stringify(this.formData);
    console.log('[webhook] POST', WEBHOOK_URL);
    console.log('[webhook] body:', this.formData);

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      const text = await res.text();
      console.log(`[webhook] response: ${res.status} ${res.statusText}`);
      console.log('[webhook] response body:', text);
      if (!res.ok) {
        console.error('[webhook] GHL rejected the request — check the URL and that the trigger is saved + workflow is published.');
      }
    } catch (err) {
      console.error('[webhook] fetch failed (CORS/network):', err);
    }
  }
}

function requireEl<T extends HTMLElement = HTMLElement>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Required element not found: ${selector}`);
  return el;
}

new SurveyEngine();
